import { Box, Button, Collapse, Stack, Tag, Text, Tooltip } from "@chakra-ui/react"
import { useMemo, useState } from "react"
import Thumbnail from "views/admin/content/components/thumbnail"
import { ContentTypeIcon, SelectionNumber } from "components/selection/common"
import { finalPlaylist, PLAYER_SKIPS_EMPTY_BROADCASTS } from "lib/playlist"
import { resolutionKey } from "lib/resolution"
import { formatSeconds } from "lib/utils"
import { sumDurations } from "lib/broadcast"

// The FINAL playlist of a composite broadcast: the order in which a device plays its clips. It is
// NOT one broadcast after the other: the Android player interleaves them with the formula in
// `lib/playlist.js#compositeOrder` (the shorter a broadcast, the wider its clips are spread), and
// this shows exactly that order. It is calculated from the list as it is now, also before saving,
// and it is only shown: nothing here changes the broadcast. Each line says which broadcast it
// comes from.
// - ids:           the broadcasts of the composite broadcast, in order
// - broadcastsById: Map(id -> broadcast), every broadcast of the client (copies included)
// - contentById:    Map(id -> content)
const PAGE = 100

export default function FinalPlaylist({ ids, broadcastsById, contentById }) {
  const [open, setOpen] = useState(false)
  const [shownCount, setShownCount] = useState(PAGE)

  // Clips whose content was deleted are left out before the order is worked out, as the player
  // does. While the content is still loading (nothing known yet) nothing is dropped.
  const plan = useMemo(
    () => finalPlaylist(ids, broadcastsById, contentById.size > 0 ? contentById : undefined),
    [ids, broadcastsById, contentById]
  )
  const { steps } = plan
  const total = useMemo(() => sumDurations(steps.map(s => s.contentId), id => contentById.get(id)?.duration), [steps, contentById])
  const emptyOnes = plan.empty
  const nestedOnes = plan.nested
  const missingOnes = plan.missing
  // The player divides by the number of clips of every broadcast in the list (step = N / n_i), so
  // a broadcast without clips is a division by zero for it (known from its code, see playlist.js)
  const playerProblem = !PLAYER_SKIPS_EMPTY_BROADCASTS && emptyOnes > 0

  return (
    <Box borderWidth='1px' borderRadius='lg' p={3} bg='gray.50'>
      <Stack direction='row' alignItems='center' wrap='wrap' gap={2} spacing={0}>
        <Text fontSize='sm' fontWeight='bold'>Final playlist</Text>
        <Tag size='sm' variant='subtle' colorScheme='gray'>{steps.length} items</Tag>
        <Tag size='sm' variant='subtle' colorScheme='cyan'>{formatSeconds(total)}</Tag>
        {emptyOnes > 0 && <Tag size='sm' variant='subtle' colorScheme='orange'>{emptyOnes} empty broadcast(s) take no part</Tag>}
        {nestedOnes > 0 && <Tag size='sm' variant='subtle' colorScheme='orange'>{nestedOnes} composite broadcast(s) take no part</Tag>}
        {plan.deletedClips > 0 && <Tag size='sm' variant='subtle' colorScheme='orange'>{plan.deletedClips} deleted clip(s) left out</Tag>}
        {missingOnes > 0 && <Tag size='sm' variant='subtle' colorScheme='red'>{missingOnes} missing</Tag>}
        <Button size='xs' variant='ghost' colorScheme='brand' ml='auto' onClick={() => setOpen(!open)} isDisabled={steps.length === 0}>
          {open ? 'Hide' : 'Show'}
        </Button>
      </Stack>
      {playerProblem && (
        <Text fontSize='xs' color='red.600' mt={1}>
          Error on the device: the released Android player cannot read a broadcast without clips yet. It divides by the number
          of clips of every broadcast in the list, so a device that gets this composite broadcast fails to synchronise and keeps
          playing its previous list. The fix is written but not released yet. The order below is what a device with the fix
          plays. Until then, remove the empty broadcast from this list.
        </Text>
      )}
      {nestedOnes > 0 && (
        <Text fontSize='xs' color='orange.600' mt={1}>
          A composite broadcast inside this one has no clips of its own, so the player leaves it out of the order, as shown here.
        </Text>
      )}
      {steps.length === 0 && <Text fontSize='xs' color='gray.500' mt={1}>Nothing to play yet: the broadcasts in the list have no content.</Text>}
      <Collapse in={open} animateOpacity unmountOnExit>
        <Stack direction='column' spacing={1} mt={2}>
          <Text fontSize='xs' color='gray.500'>
            The order in which a device plays the clips, from the first to the last, then it starts again. The broadcasts are
            interleaved, not played one after the other: a shorter broadcast has its clips spread wider. A clip that a device
            could not download is left out of the list after the order is worked out.
          </Text>
          {steps.slice(0, shownCount).map((step, index) => {
            const item = contentById.get(step.contentId)
            const resolution = item ? resolutionKey(item) : null
            return (
              <Stack key={index} direction='row' alignItems='center' spacing={2} borderWidth='1px' borderRadius='md' bg='white' px={2} py={1}>
                <SelectionNumber value={index + 1} />
                {item ? (
                  <>
                    <Box flexShrink={0}><Thumbnail content={item} /></Box>
                    <ContentTypeIcon type={item.type} />
                    <Text fontSize='sm' noOfLines={1} flex='1' minW={0}>{item.displayName || item.filename}</Text>
                    {resolution && <Tag size='sm' variant='subtle'>{resolution}</Tag>}
                    <Tag size='sm' variant='subtle' colorScheme='cyan'>{formatSeconds(item.duration || 0)}</Tag>
                  </>
                ) : (
                  <Text fontSize='sm' color='red.500' flex='1'>Content no longer exists</Text>
                )}
                <Tooltip label={`The broadcast this item comes from. Its place in the formula: ${step.position}`}>
                  <Tag size='sm' variant='outline' colorScheme='brand' maxW='160px'><Text as='span' isTruncated>{broadcastsById.get(step.broadcastId)?.name || '?'}</Text></Tag>
                </Tooltip>
              </Stack>
            )
          })}
          {steps.length > shownCount && (
            <Button size='xs' variant='ghost' colorScheme='brand' onClick={() => setShownCount(count => count + PAGE)}>
              Show more ({steps.length - shownCount} left)
            </Button>
          )}
        </Stack>
      </Collapse>
    </Box>
  )
}
